import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { pageImageUrl } from "../lib/api";
import { fileName } from "../lib/words";
import { Link } from "react-router-dom";
import { documentOpenPath } from "../pages/documentViewFlow";
import { Dialog } from "./Dialog";

/**
 * The source page, with the cited region drawn over it.
 *
 * The region arrives as fractions of the page, so it is positioned in percentages and stays
 * correct at any zoom or render resolution. This is the screen that decides whether an
 * engineer believes the extraction: a value with a page number is a claim, a value you can
 * see on the page is evidence.
 *
 * The file is named by its file name and page. The server's folders mean nothing to the reader
 * and are not shown.
 */
export function DocumentViewer({
  path, page, box, label, onClose, pkg = null,
}: {
  path: string; page: number;
  /** The package, when the screen knows it; the document view link searches every package otherwise. */
  pkg?: string | null;
  box: { x: number; y: number; width: number; height: number } | null;
  label: string; onClose: () => void;
}) {
  const [p, setP] = useState(page);
  const name = fileName(path);
  return (
    <Dialog
      title={<>{name} <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>page {p}</span></>}
      onClose={onClose}
      headExtra={
        <div className="pager" style={{ marginLeft: "auto" }}>
          <button type="button" className="ghost" aria-label="Previous page"
                  onClick={() => setP((v) => Math.max(1, v - 1))} disabled={p <= 1}>
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button type="button" className="ghost" aria-label="Next page" onClick={() => setP((v) => v + 1)}>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      }
    >
      <div className="body">
        <div className="viewer">
          <div className="page-wrap" style={{ background: "var(--color-paper)" }}>
            <img src={pageImageUrl(path, p)} alt={`${name}, page ${p}`} />
            {box && p === page && (
              <div className="citation" style={{
                left: `${box.x * 100}%`, top: `${box.y * 100}%`,
                width: `${box.width * 100}%`, height: `${box.height * 100}%`,
              }} />
            )}
          </div>
          <dl className="meta">
            <dt>Cited value</dt><dd>{label}</dd>
            <dt>Page</dt><dd>{page}</dd>
            <dt>Region</dt>
            <dd>{box
              ? `${(box.x * 100).toFixed(1)}%, ${(box.y * 100).toFixed(1)}%`
              : "not marked on the page"}</dd>
            <dt>File</dt><dd className="file-name">{name}</dd>
            <dt>Document</dt>
            <dd><Link to={documentOpenPath({ path, pkg, page: p }) ?? "/documents"}>Open in the document view</Link></dd>
          </dl>
        </div>
      </div>
    </Dialog>
  );
}
